import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import { PageSEO } from "../hooks/useSEO";
import "../styles/styles.css";

const AIPolicy = () => {
  const { lang } = useLang();
  const isEN = lang === "en";

  const sections = isEN ? [
    {
      title: "1. What Is AI-Generated Content?",
      body: "Content on AIVEO is produced using artificial intelligence tools — including large language models, image generation systems, audio synthesis and video generation — under the supervision of human editors. 'AI-generated' means the primary creative output (script, visuals, audio) is produced by an AI model, directed and curated by a human team.",
    },
    {
      title: "2. Editorial Oversight",
      body: "All AI-generated content published on AIVEO is reviewed by a human editorial team before publication. We do not publish raw, unreviewed AI output. Our editors evaluate content for quality, accuracy, appropriateness and potential rights conflicts before approval.",
    },
    {
      title: "3. Copyright and Rights",
      body: "AIVEO asserts copyright over its AI-generated works to the extent permitted by applicable law. We do not intentionally use copyrighted training prompts or reference protected works without transformation. Where AI tools are used that have their own terms of service regarding commercial output, we operate within those terms. If you believe any content on AIVEO infringes your rights, contact us at aiveoaicinema@gmail.com for immediate review.",
    },
    {
      title: "4. Prohibited Content",
      body: "AIVEO does not produce or allow: (a) Content that sexualises minors or could constitute CSAM. (b) Content that incites violence against real individuals or groups. (c) Defamatory content about real, identifiable people. (d) Content that impersonates real brands, public figures or protected characters without transformation. (e) Disinformation presented as factual news. (f) Content that promotes hate, discrimination or illegal activity.",
    },
    {
      title: "5. Creator Responsibilities",
      body: "Creators participating in the IA Creators program must: (a) Not submit prompts or source material that incorporates protected third-party content without authorisation. (b) Declare if their submission uses identifiable real people, brands or events. (c) Not use AIVEO's platform to generate prohibited content as defined above. (d) Accept that submitted content may be refused or removed at AIVEO's sole discretion.",
    },
    {
      title: "6. Transparency",
      body: "AIVEO labels all content as AI-generated. We do not present AI content as human-produced without disclosure. We are committed to developing clearer labelling standards as the technology and regulation evolve.",
    },
    {
      title: "7. Bias and Accuracy",
      body: "AI models can reflect biases present in their training data. AIVEO actively reviews content for harmful stereotypes, discriminatory portrayals and factual inaccuracies. We acknowledge this is an imperfect process during our beta phase and welcome reports of problematic content.",
    },
    {
      title: "8. Reporting",
      body: "To report content you believe violates this policy, contact aiveoaicinema@gmail.com with the title or URL of the content and the reason for your report. We will investigate and respond within 5 business days.",
    },
    {
      title: "9. Policy Evolution",
      body: "This AI Content Policy will be updated as AI technology, legal frameworks and platform capabilities evolve. Significant changes will be announced on the platform.",
    },
  ] : [
    {
      title: "1. ¿Qué es el Contenido Generado por IA?",
      body: "El contenido de AIVEO se produce utilizando herramientas de inteligencia artificial — incluyendo grandes modelos de lenguaje, sistemas de generación de imágenes, síntesis de audio y generación de vídeo — bajo la supervisión de editores humanos. 'Generado por IA' significa que la producción creativa principal (guión, visuales, audio) es producida por un modelo de IA, dirigida y curada por un equipo humano.",
    },
    {
      title: "2. Supervisión Editorial",
      body: "Todo el contenido generado por IA publicado en AIVEO es revisado por un equipo editorial humano antes de su publicación. No publicamos resultados de IA en bruto sin revisar. Nuestros editores evalúan el contenido en cuanto a calidad, precisión, adecuación y posibles conflictos de derechos antes de la aprobación.",
    },
    {
      title: "3. Derechos de Autor y Derechos",
      body: "AIVEO reivindica los derechos de autor sobre sus obras generadas por IA en la medida permitida por la legislación aplicable. No usamos intencionadamente prompts de entrenamiento con derechos de autor ni referenciamos obras protegidas sin transformación. Donde se usan herramientas de IA que tienen sus propios términos de servicio respecto a la producción comercial, operamos dentro de esos términos. Si crees que algún contenido de AIVEO infringe tus derechos, contáctanos en aiveoaicinema@gmail.com para una revisión inmediata.",
    },
    {
      title: "4. Contenido Prohibido",
      body: "AIVEO no produce ni permite: (a) Contenido que sexualice a menores o pueda constituir CSAM. (b) Contenido que incite a la violencia contra personas o grupos reales. (c) Contenido difamatorio sobre personas reales e identificables. (d) Contenido que suplante marcas reales, figuras públicas o personajes protegidos sin transformación. (e) Desinformación presentada como noticias factuales. (f) Contenido que promueva el odio, la discriminación o la actividad ilegal.",
    },
    {
      title: "5. Responsabilidades de los Creadores",
      body: "Los creadores que participen en el programa IA Creators deben: (a) No enviar prompts o material fuente que incorpore contenido protegido de terceros sin autorización. (b) Declarar si su envío usa personas reales identificables, marcas o eventos. (c) No usar la plataforma de AIVEO para generar contenido prohibido según lo definido anteriormente. (d) Aceptar que el contenido enviado puede ser rechazado o eliminado a discreción exclusiva de AIVEO.",
    },
    {
      title: "6. Transparencia",
      body: "AIVEO etiqueta todo el contenido como generado por IA. No presentamos contenido de IA como producido por humanos sin divulgación. Estamos comprometidos a desarrollar estándares de etiquetado más claros a medida que la tecnología y la regulación evolucionen.",
    },
    {
      title: "7. Sesgo y Precisión",
      body: "Los modelos de IA pueden reflejar sesgos presentes en sus datos de entrenamiento. AIVEO revisa activamente el contenido en busca de estereotipos dañinos, representaciones discriminatorias e inexactitudes factuales. Reconocemos que este es un proceso imperfecto durante nuestra fase beta y agradecemos los informes de contenido problemático.",
    },
    {
      title: "8. Denuncias",
      body: "Para denunciar contenido que creas que viola esta política, contacta con aiveoaicinema@gmail.com con el título o URL del contenido y el motivo de tu denuncia. Investigaremos y responderemos en un plazo de 5 días hábiles.",
    },
    {
      title: "9. Evolución de la Política",
      body: "Esta Política de Contenido IA se actualizará a medida que evolucionen la tecnología de IA, los marcos legales y las capacidades de la plataforma. Los cambios significativos se anunciarán en la plataforma.",
    },
  ];

  return (
    <div className="legal-page">
      <PageSEO
        title={isEN ? "AI Content Policy" : "Política de Contenido IA"}
        description={isEN ? "AIVEO's responsible AI content policy — how we create, moderate and label AI-generated content." : "Política de contenido IA de AIVEO — cómo creamos, moderamos y etiquetamos contenido generado con inteligencia artificial."}
        url="/ai-policy"
      />
      <div className="legal-container">
        <div className="legal-hero">
          <h1>{isEN ? "AI Content Policy" : "Política de Contenido IA"}</h1>
          <p className="legal-meta">{isEN ? "Last updated: June 2025" : "Última actualización: Junio 2025"}</p>
        </div>
        {sections.map((s) => (
          <section key={s.title} className="legal-section">
            <h2>{s.title}</h2>
            <p>{s.body}</p>
          </section>
        ))}
        <div className="legal-footer-nav">
          <Link to="/about"   className="legal-nav-link">{isEN ? "About" : "Sobre AIVEO"}</Link>
          <Link to="/terms"   className="legal-nav-link">{isEN ? "Terms of Use" : "Términos de Uso"}</Link>
          <Link to="/privacy" className="legal-nav-link">{isEN ? "Privacy Policy" : "Política de Privacidad"}</Link>
        </div>
      </div>
    </div>
  );
};

export default AIPolicy;
